import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import TheQAHubAcademyImage from "../images/TheQAHubAcademy.webp";
import { LANGUAGES, useTranslation } from "../i18n";
import { clearAuthToken, getAuthHeader } from "../utils/auth";
import { fetchCurrentUser, getApiBaseUrl } from "../utils/api";
import "./Header.css";

const API_URL = getApiBaseUrl();

const getDisplayName = (user) => {
  if (!user) return "";

  const nombre = (user.nombre || user.name || user.firstName || "").trim();
  const apellidos = (user.apellidos || user.lastName || user.surname || "").trim();
  const apellidoInicial = apellidos ? `${apellidos.charAt(0).toUpperCase()}.` : "";

  if (nombre) return `${nombre} ${apellidoInicial}`.trim();
  if (user.username) return user.username;
  if (user.email) return user.email.split("@")[0];
  return "";
};

function Header() {
  const { language, setLanguage, t } = useTranslation();
  const [scrolling, setScrolling] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Dashboard dropdown
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const isRoadmap = location.pathname === "/roadmap" || location.pathname.startsWith("/roadmap/");
  const isLearnCourse = location.pathname.startsWith("/learn/");
  const isSettings = location.pathname === "/settings";
  const isAcademyLanding = location.pathname === "/academy";
  const isLegacyAcademy = location.pathname === "/academy-legacy";
  const isAcademySection = isAcademyLanding || isLegacyAcademy;
  const isBugsPage = location.pathname === "/bugs" || location.pathname === "/bug-dictionary";
  const isAIHubPage = location.pathname === "/ai" || location.pathname === "/ai-hub";
  const isTestsHubPage = location.pathname === "/tests" || location.pathname === "/test-hub";
  const isDashboard =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/learn/") ||
    location.pathname.startsWith("/retos") ||
    location.pathname.startsWith("/retos/reto1") ||
    location.pathname.startsWith("/retos/reto2") ||
    location.pathname.startsWith("/retos/reto3");

  const isLogin = location.pathname === "/login";

  // Si la ruta es otra, agregamos una clase diferente
  const headerClass = `header ${scrolling ? "scrolled" : ""} 
  ${location.pathname === "/cookies-policy" ? "cookies-header" : ""} 
  ${location.pathname === "/privacy-policy" ? "privacy-header" : ""} 
  ${location.pathname === "/login" ? "login-header" : ""} 
  ${location.pathname === "/academy" ? "academy-header" : ""} 
  ${location.pathname === "/academy-legacy" ? "academy-header" : ""} 
  ${location.pathname === "/bugs" ? "bugs-header" : ""}
  ${location.pathname === "/bug-dictionary" ? "bugs-header" : ""}
  ${location.pathname === "/ai" ? "tests-header" : ""}
  ${location.pathname === "/ai-hub" ? "tests-header" : ""}
  ${location.pathname === "/tests" ? "tests-header" : ""}
  ${location.pathname === "/test-hub" ? "tests-header" : ""}
  ${location.pathname === "/dashboard" ? "dashboard-header" : ""}
  ${location.pathname === "/retos" ? "dashboard-header" : ""} 
  ${location.pathname === "/retos/reto1" ? "dashboard-header" : ""}
  ${location.pathname === "/retos/reto2" ? "dashboard-header" : ""}
  ${location.pathname === "/retos/reto3" ? "dashboard-header" : ""}
  ${location.pathname.startsWith("/learn/") ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/fundamentos" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/manual" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/automation" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/api" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/cicd" ? "dashboard-header" : ""}
  ${location.pathname === "/roadmap/buenas" ? "dashboard-header" : ""}
  ${location.pathname === "/settings" ? "settings-header" : ""}`;

  const goAcademy = () => {
    navigate("/academy");
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolling(window.scrollY > 50);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    if (
      isDashboard ||
      isLogin ||
      isSettings ||
      isRoadmap ||
      isLearnCourse ||
      isAcademySection ||
      isBugsPage ||
      isAIHubPage ||
      isTestsHubPage
    ) {
      document.documentElement.style.setProperty("--header-height", "var(--academy-header-height)");
    } else {
      document.documentElement.style.setProperty("--header-height", "0px");
    }

    // Inicialización
    handleScroll();
    handleResize();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

      const loadUser = async () => {
      // ✅ antes: solo dashboard/settings
      // ✅ ahora: también roadmap
      if (!(isDashboard || isSettings || isRoadmap || isLearnCourse)) return;

      const { user: currentUser } = await fetchCurrentUser();
      setUser(currentUser);
    };

    loadUser();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      document.documentElement.style.setProperty("--header-height", "0px");
    };
  }, [
    isDashboard,
    isLogin,
    isSettings,
    isRoadmap,
    isLearnCourse,
    isAcademyLanding,
    isLegacyAcademy,
    isAcademySection,
    isBugsPage,
    isAIHubPage,
    isTestsHubPage,
  ]);

  const displayName = getDisplayName(user) || "Perfil";

  const avatarUrl = user?.avatarUrl || "";

  useEffect(() => {
    setAvatarError(false);
  }, [avatarUrl]);

  const getAvatarSrc = (url) => {
    if (!url) return "";
    if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) {
      return url;
    }
    return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const onLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeader(),
        },
      });
    } catch (e) {}
    clearAuthToken();
    navigate("/login");
  };

  const LanguageSwitcher = () => (
    <div className="language-switcher" aria-label="Language selector">
      {LANGUAGES.map((item) => (
        <button
          key={item.code}
          type="button"
          className={language === item.code ? "active" : ""}
          onClick={() => setLanguage(item.code)}
          aria-label={item.name}
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  const getNavLinkClassName = ({ isActive }) => (isActive ? "active" : "");

  return (
    <>
      {isSettings ? (
        // HEADER SETTINGS
        <header className={headerClass}>
          <div className="dashboard-left">
            <NavLink to="/dashboard" className="dashboard-logo-link">
              <img className="dashboard-logo" src={TheQAHubAcademyImage} alt="TheQAHub Academy" />
            </NavLink>
          </div>

          <div className="dashboard-right">
            <button
              type="button"
              className="dashboard-user"
              onClick={() => setIsUserMenuOpen((v) => !v)}
            >
              <span className="avatar-circle">
                {avatarUrl && !avatarError ? (
                  <img
                    src={getAvatarSrc(avatarUrl)}
                    alt="Avatar"
                    className="avatar-img"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  "👤"
                )}
              </span>
              <span className="user-name">{displayName}</span>
              <span className="chev">▾</span>
            </button>

            {isUserMenuOpen && (
              <div className="dashboard-dropdown">
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    navigate("/dashboard");
                    setIsUserMenuOpen((v) => !v);
                  }}
                >
                  {t("nav.dashboard")}
                </button>
                <button type="button" className="dropdown-item danger" onClick={onLogout}>
                  {t("nav.logout")}
                </button>
              </div>
            )}
          </div>
        </header>
      ) : (
        <>
          {isDashboard || isRoadmap ? (
            // HEADER DASHBOARD/ROADMAP
            <header className={headerClass}>
              <div className="dashboard-left">
                <NavLink to="/dashboard" className="dashboard-logo-link">
                  <img className="dashboard-logo" src={TheQAHubAcademyImage} alt="TheQAHub Academy" />
                </NavLink>
              </div>

              <div className="dashboard-right">
                <button
                  type="button"
                  className="dashboard-user"
                  onClick={() => setIsUserMenuOpen((v) => !v)}
                >
                  <span className="avatar-circle">
                    {avatarUrl && !avatarError ? (
                      <img
                        src={getAvatarSrc(avatarUrl)}
                        alt="Avatar"
                        className="avatar-img"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      "👤"
                    )}
                  </span>
                  <span className="user-name">{displayName}</span>
                  <span className="chev">▾</span>
                </button>

                {isUserMenuOpen && (
                  <div className="dashboard-dropdown">
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        navigate("/settings");
                        setIsUserMenuOpen((v) => !v);
                      }}
                    >
                      {t("nav.settings")}
                    </button>
                    <button type="button" className="dropdown-item danger" onClick={onLogout}>
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            </header>
          ) : (
            // HEADER NORMAL (movil o web)
            <>
              {isMobile ? (
                <header className={headerClass}>
                  <div className="logo" translate="no">
                    <span className="theqahub-font-mobile">{"</"}TheQAHub{">"}</span>
                  </div>
                  <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    ☰
                  </button>
                  <LanguageSwitcher />

                  {/* Menú desplegable en móvil */}
                  {isMenuOpen && (
                    <nav className="mobile-nav">
                      <ul className="nav-links-mobile">
                        <li><NavLink to="/" onClick={() => setIsMenuOpen(false)}>{t("nav.home")}</NavLink></li>
                        <li><NavLink to="/sobre-mi" onClick={() => setIsMenuOpen(false)}>{t("nav.about")}</NavLink></li>
                        <li><NavLink to="/blog" onClick={() => setIsMenuOpen(false)}>{t("nav.blog")}</NavLink></li>
                        <li><NavLink to="/ai-hub" onClick={() => setIsMenuOpen(false)}>{t("nav.ai")}</NavLink></li>
                        <li><NavLink to="/test-hub" onClick={() => setIsMenuOpen(false)}>{t("nav.tests")}</NavLink></li>
                        <li><NavLink to="/bugs" onClick={() => setIsMenuOpen(false)}>{t("nav.bugs")}</NavLink></li>
                        <li><NavLink to="/aprende" onClick={() => setIsMenuOpen(false)}>{t("nav.learn")}</NavLink></li>
                        <li><NavLink to="/contacto" onClick={() => setIsMenuOpen(false)}>{t("nav.contact")}</NavLink></li>

                        {/* ✅ CAMBIO: antes era NavLink a /login */}
                        <li>
                          <button
                            type="button"
                            onClick={goAcademy}
                            className={`academy-link ${isAcademySection ? "active" : ""}`}
                          >
                            <span className="theqahub-font">{"</"}TheQAHub{">"} Academy</span>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </header>
              ) : (
                <header className={headerClass}>
                  <div className="logo" translate="no">
                    <span className="theqahub-font">{"</"}TheQAHub{">"}</span>
                  </div>
                  <nav>
                    <ul className="nav-links">
                      <li>
                        <NavLink to="/" className={getNavLinkClassName}>
                          {t("nav.home")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/sobre-mi" className={getNavLinkClassName}>
                          {t("nav.about")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/blog" className={getNavLinkClassName}>
                          {t("nav.blog")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/ai-hub" className={getNavLinkClassName}>
                          {t("nav.ai")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/test-hub" className={getNavLinkClassName}>
                          {t("nav.tests")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/bugs" className={getNavLinkClassName}>
                          {t("nav.bugs")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/aprende" className={getNavLinkClassName}>
                          {t("nav.learn")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/contacto" className={getNavLinkClassName}>
                          {t("nav.contact")}
                        </NavLink>
                      </li>

                      <li>
                        <button
                          type="button"
                          className={`academy-link ${isAcademySection ? "active" : ""}`}
                          onClick={goAcademy}
                          aria-label={t("nav.academyLabel")}
                        >
                          <img
                            className="theqahubAcademyImage"
                            src={TheQAHubAcademyImage}
                            alt="TheQAHub Academy"
                          />
                        </button>
                      </li>
                      <li>
                        <LanguageSwitcher />
                      </li>
                    </ul>
                  </nav>
                </header>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default Header;
